import React, { useState, useCallback } from 'react';
import { Upload, Type, ScanLine, History, Copy, Trash2, Lightbulb, Sparkles } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useDropzone } from 'react-dropzone';
import { useAuthStore } from '../store/authStore';
import { canWriteRecommendations } from '../utils/roles';
import axios from 'axios';
import toast from 'react-hot-toast';

function Scanner() {
  const [activeTab, setActiveTab] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [scanUsers, setScanUsers] = useState([]);
  const [historyUserFilter, setHistoryUserFilter] = useState('');
  const [symbolDetailsModal, setSymbolDetailsModal] = useState(null);
  const [symbolRecommendation, setSymbolRecommendation] = useState(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);
  const [generatingRecommendation, setGeneratingRecommendation] = useState(false);
  const [availableRecommendations, setAvailableRecommendations] = useState({});
  const [scanStartTime, setScanStartTime] = useState(null);
  const [scanElapsedTime, setScanElapsedTime] = useState(0);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailFrom, setEmailFrom] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [emailAdvice, setEmailAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const { isAuthenticated, user } = useAuthStore();

  React.useEffect(() => {
    let interval = null;
    if (isScanning && scanStartTime) {
      interval = setInterval(() => setScanElapsedTime(Math.floor((Date.now() - scanStartTime) / 1000)), 1000);
    } else { setScanElapsedTime(0); }
    return () => { if (interval) clearInterval(interval); };
  }, [isScanning, scanStartTime]);

  const getScanProgressMessage = () => {
    if (!isScanning) return '';
    if (scanElapsedTime < 10) return `Scanning... (${scanElapsedTime}s)`;
    if (scanElapsedTime < 30) return `Analyzing content... (${scanElapsedTime}s)`;
    if (scanElapsedTime < 60) return `Deep analysis... (${scanElapsedTime}s)`;
    return `Finalizing... (${scanElapsedTime}s, ${Math.max(0, 90 - scanElapsedTime)}s remaining)`;
  };

  React.useEffect(() => {
    if (scanResult) fetchAvailableRecommendations(scanResult);
    else setAvailableRecommendations({});
  }, [scanResult]);

  const onDrop = useCallback((files) => { if (files[0]) { setSelectedFile(files[0]); toast.success(`File: ${files[0].name}`); } }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'message/rfc822': ['.eml'], 'application/vnd.ms-outlook': ['.msg'], 'text/plain': ['.txt'], 'text/html': ['.html'] }, maxFiles: 1, maxSize: 10 * 1024 * 1024
  });

  const scanEmailContent = async () => {
    if (!emailContent.trim()) { toast.error('Please enter email content'); return; }
    setIsScanning(true); setScanResult(null); setScanStartTime(Date.now());
    try { const r = await axios.post('/scan/email', { content: emailContent, subject: emailSubject, from: emailFrom, to: emailTo }); setScanResult(r.data); toast.success('Scanned!'); }
    catch (e) { toast.error(e.response?.data?.message || 'Scan failed'); }
    finally { setIsScanning(false); setScanStartTime(null); setScanElapsedTime(0); }
  };

  const scanFile = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    setIsScanning(true); setScanResult(null); setScanStartTime(Date.now());
    const fd = new FormData(); fd.append('email', selectedFile);
    try { const r = await axios.post('/scan/file', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setScanResult(r.data); toast.success(`Scanned ${selectedFile.name}!`); }
    catch (e) { toast.error(e.response?.data?.message || 'Scan failed'); }
    finally { setIsScanning(false); setScanStartTime(null); setScanElapsedTime(0); }
  };

  const isAdmin = canWriteRecommendations(user);

  const fetchScanHistory = async (userFilter) => {
    try {
      const params = new URLSearchParams();
      if (userFilter) params.set('userId', userFilter);
      const r = await axios.get('/scan/history?' + params.toString());
      setScanHistory(r.data.scans || []);
      setShowHistory(true);
      if (isAdmin && scanUsers.length === 0) {
        try { const u = await axios.get('/scan/history/users'); setScanUsers(u.data.users || []); } catch {}
      }
    }
    catch { toast.error('Failed to fetch history'); }
  };

  const getScoreDisplay = (score, req = 15, action = null, engine = 'generic') => {
    let spam = false;
    if (engine === 'rspamd' && action) { spam = !['greylist', 'no action', 'no_action'].includes(action.toLowerCase().replace(/\s+/g, '_')); }
    else if (engine === 'overall') { spam = score >= req || (action && !['greylist', 'no action', 'no_action'].includes(action.toLowerCase().replace(/\s+/g, '_'))); }
    else { spam = score >= req; }
    if (spam) return { label: 'SPAM', badge: 'bg-red-100 text-red-700', bg: 'bg-red-50' };
    if (score >= req * 0.7) return { label: 'SUSPICIOUS', badge: 'bg-yellow-100 text-yellow-700', bg: 'bg-yellow-50' };
    return { label: 'CLEAN', badge: 'bg-green-100 text-green-700', bg: 'bg-green-50' };
  };

  const clearResults = () => { setScanResult(null); setEmailContent(''); setEmailSubject(''); setEmailFrom(''); setEmailTo(''); setSelectedFile(null); setScanStartTime(null); setScanElapsedTime(0); setAvailableRecommendations({}); setEmailAdvice(null); };

  const fetchAdvice = async () => {
    if (!scanResult) return;
    try {
      setLoadingAdvice(true);
      const r = await axios.post('/scan/advice', { scanResult });
      setEmailAdvice(r.data.advice);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to get advice'); }
    finally { setLoadingAdvice(false); }
  };

  const fetchAvailableRecommendations = async (sr) => {
    const symbols = [];
    if (sr.results?.rspamd?.symbols) Object.keys(sr.results.rspamd.symbols).forEach(n => symbols.push({ name: n, engine: 'rspamd' }));
    if (sr.results?.spamassassin?.symbols) sr.results.spamassassin.symbols.forEach(n => symbols.push({ name: n, engine: 'spamassassin' }));
    if (!symbols.length) return;
    try { const r = await axios.post('/recommendations/symbols/batch', { symbols }); setAvailableRecommendations(r.data.recommendations || {}); }
    catch { setAvailableRecommendations({}); }
  };

  const hasRec = (name, engine) => availableRecommendations[`${name}_${engine}`] !== undefined;
  const recCount = (engine, symbols) => {
    if (!symbols) return 0;
    if (engine === 'rspamd' && typeof symbols === 'object') return Object.keys(symbols).filter(s => hasRec(s, engine)).length;
    if (engine === 'spamassassin' && Array.isArray(symbols)) return symbols.filter(s => hasRec(s, engine)).length;
    return 0;
  };

  const openSymbolDetails = (symbol, data, engine) => { setSymbolDetailsModal({ symbol, data, engine }); setSymbolRecommendation(null); fetchSymbolRecommendation(symbol, engine); };
  const closeSymbolDetails = () => { setSymbolDetailsModal(null); setSymbolRecommendation(null); };

  const fetchSymbolRecommendation = async (symbol, engine) => {
    try { setLoadingRecommendation(true); const r = await axios.get(`/recommendations/symbol/${engine}/${encodeURIComponent(symbol)}`); setSymbolRecommendation(r.data.recommendation); }
    catch { setSymbolRecommendation(null); }
    finally { setLoadingRecommendation(false); }
  };

  const generateRecommendation = async () => {
    if (!symbolDetailsModal) return;
    const { symbol, data, engine } = symbolDetailsModal;
    try {
      setGeneratingRecommendation(true);
      const r = await axios.post('/recommendations/generate', { symbolName: symbol, engine, symbolData: data });
      setSymbolRecommendation(r.data.recommendation);
      setAvailableRecommendations(prev => ({ ...prev, [`${symbol}_${engine}`]: true }));
      toast.success(r.data.source === 'generated' ? 'Recommendation generated and saved' : 'Recommendation found');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate recommendation');
    } finally { setGeneratingRecommendation(false); }
  };

  const Spinner = () => <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />;

  if (!isAuthenticated) return (
    <div className="text-center py-16"><ScanLine className="w-20 h-20 text-gray-300 mx-auto mb-4" /><p className="text-xl text-gray-500">Please log in to use the email scanner</p></div>
  );

  return (
    <>
      <Helmet><title>Email Scanner - Rspamd Email Scanner</title></Helmet>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-1">Email Scanner</h2>
            <p className="text-gray-500">Scan newsletters and emails for spam using Rspamd and SpamAssassin.</p>
          </div>
          <div className="flex gap-1">
            <button onClick={fetchScanHistory} className="p-2 rounded-lg hover:bg-gray-100 text-blue-600" title="History"><History className="w-5 h-5" /></button>
            <button onClick={clearResults} className="p-2 rounded-lg hover:bg-gray-100 text-pink-600" title="Clear"><Trash2 className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input panel */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex border-b mb-4">
              {[{ icon: Type, label: 'Paste Content' }, { icon: Upload, label: 'Upload File' }].map((tab, i) => (
                <button key={i} onClick={() => setActiveTab(i)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${activeTab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <tab.icon className="w-4 h-4" />{tab.label}
                </button>
              ))}
            </div>

            {activeTab === 0 && (
              <div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input value={emailFrom} onChange={e => setEmailFrom(e.target.value)} placeholder="From" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                  <input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="To" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Subject" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-blue-500 outline-none" />
                <textarea value={emailContent} onChange={e => setEmailContent(e.target.value)} rows={8} placeholder="Paste email content here..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 resize-y focus:ring-2 focus:ring-blue-500 outline-none" />
                <button onClick={scanEmailContent} disabled={isScanning || !emailContent.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50">
                  {isScanning ? <Spinner /> : <ScanLine className="w-5 h-5" />}
                  {isScanning ? getScanProgressMessage() : 'Scan Email Content'}
                </button>
              </div>
            )}

            {activeTab === 1 && (
              <div>
                <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer mb-3 transition ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}>
                  <input {...getInputProps()} />
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="font-semibold">{isDragActive ? 'Drop file here' : 'Drag & drop email file'}</p>
                  <p className="text-sm text-gray-500">.eml, .msg, .txt, .html (max 10MB)</p>
                  {selectedFile && (
                    <span className="inline-flex items-center gap-1 mt-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                      {selectedFile.name}
                      <button onClick={e => { e.stopPropagation(); setSelectedFile(null); }} className="ml-1 hover:text-blue-900">&times;</button>
                    </span>
                  )}
                </div>
                <button onClick={scanFile} disabled={isScanning || !selectedFile}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50">
                  {isScanning ? <Spinner /> : <ScanLine className="w-5 h-5" />}
                  {isScanning ? getScanProgressMessage() : 'Scan File'}
                </button>
              </div>
            )}
          </div>

          {/* Results panel */}
          {scanResult ? (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Overall Result</h3>
                <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(scanResult, null, 2)); toast.success('Copied!'); }} className="text-gray-400 hover:text-blue-600"><Copy className="w-5 h-5" /></button>
              </div>
              {scanResult.overall && (() => {
                const d = getScoreDisplay(scanResult.overall.score, 15, scanResult.overall.action, 'overall');
                return (
                  <div className={`${d.bg} rounded-lg p-6 text-center`}>
                    <p className="text-4xl font-bold mb-2">{scanResult.overall.score?.toFixed(2) || '0.00'}</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${d.badge}`}>{d.label}</span>
                    <p className="text-sm text-gray-600 mt-2">Action: {scanResult.overall.action} | Confidence: {scanResult.overall.confidence}</p>
                    <p className="text-sm text-gray-500">{scanResult.overall.summary}</p>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border p-6 flex flex-col items-center justify-center text-center">
              <ScanLine className="w-14 h-14 text-gray-300 mb-3" />
              <p className="text-lg text-gray-500 font-medium mb-2">Scan results will appear here</p>
              <p className="text-sm text-gray-400 mb-4">Upload a file or paste email content to get started</p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded text-left text-sm w-full">
                <p className="font-semibold mb-1">Alternative: Email Forwarding</p>
                <p className="text-gray-600 mb-1">Forward emails to your scan address for analysis:</p>
                <code className="block bg-blue-100 p-2 rounded text-sm">{user?.email ? `${user.email.replace('@', '.')}@scan.rspamd.net` : 'your.email@scan.rspamd.net'}</code>
              </div>
            </div>
          )}
        </div>

        {/* Email advice panel */}
        {scanResult && (
          <div className="mt-6">
            {!emailAdvice && !loadingAdvice ? (
              <button onClick={fetchAdvice} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition shadow-sm">
                <Sparkles className="w-5 h-5" /> Get AI advice on improving this email
              </button>
            ) : loadingAdvice ? (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center gap-2 mb-4"><Sparkles className="w-5 h-5 text-purple-600 animate-pulse" /><h3 className="text-lg font-semibold">Analyzing email...</h3></div>
                <div className="space-y-3"><div className="h-5 w-3/4 bg-gray-200 rounded animate-pulse" /><div className="h-4 w-full bg-gray-200 rounded animate-pulse" /><div className="h-20 bg-gray-100 rounded animate-pulse" /><div className="h-20 bg-gray-100 rounded animate-pulse" /></div>
              </div>
            ) : emailAdvice && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /><h3 className="text-lg font-semibold">AI Deliverability Advice</h3></div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${emailAdvice.overall_verdict === 'pass' ? 'bg-green-100 text-green-700' : emailAdvice.overall_verdict === 'risky' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                    {emailAdvice.overall_verdict?.toUpperCase()}
                  </span>
                </div>
                {emailAdvice.summary && <p className="text-gray-700 mb-2">{emailAdvice.summary}</p>}
                {emailAdvice.score_explanation && <p className="text-sm text-gray-500 mb-4">{emailAdvice.score_explanation}</p>}

                {emailAdvice.issues?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-sm mb-2">Issues to fix</h4>
                    <div className="space-y-2">
                      {emailAdvice.issues.map((issue, i) => (
                        <div key={i} className={`border-l-4 rounded-r-lg p-3 text-sm ${issue.priority === 'critical' ? 'border-red-500 bg-red-50' : issue.priority === 'high' ? 'border-orange-500 bg-orange-50' : issue.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold uppercase ${issue.priority === 'critical' ? 'bg-red-200 text-red-800' : issue.priority === 'high' ? 'bg-orange-200 text-orange-800' : issue.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'}`}>{issue.priority}</span>
                            <span className="px-1.5 py-0.5 rounded text-xs border text-gray-600">{issue.area}</span>
                          </div>
                          <p className="font-medium mb-0.5">{issue.problem}</p>
                          <p className="text-gray-600">{issue.fix}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {emailAdvice.quick_wins?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-sm text-green-800 mb-2">Quick wins</h4>
                    <ul className="space-y-1 text-sm text-green-700">
                      {emailAdvice.quick_wins.map((item, i) => <li key={i} className="flex items-start gap-2"><span className="text-green-500 mt-0.5">&#10003;</span>{item}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Detailed results */}
        {scanResult && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Rspamd */}
            {scanResult.results?.rspamd && (
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-blue-600">Rspamd Results</h4>
                  {recCount('rspamd', scanResult.results.rspamd.symbols) > 0 && (
                    <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"><Lightbulb className="w-3 h-3" />{recCount('rspamd', scanResult.results.rspamd.symbols)} tips</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                  <div><span className="text-gray-500 block">Score</span><span className="text-lg font-bold">{scanResult.results.rspamd.score?.toFixed(2) || '0'}</span></div>
                  <div><span className="text-gray-500 block">Action</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getScoreDisplay(scanResult.results.rspamd.score, 15, scanResult.results.rspamd.action, 'rspamd').badge}`}>{scanResult.results.rspamd.action}</span></div>
                  <div><span className="text-gray-500 block">Rules</span><span className="text-lg font-bold">{scanResult.results.rspamd.rules_matched || 0}</span></div>
                  <div><span className="text-gray-500 block">Time</span><span className="text-lg font-bold">{(scanResult.results.rspamd.scan_time * 1000)?.toFixed(0) || '0'}ms</span></div>
                </div>
                {scanResult.results.rspamd.symbols && Object.keys(scanResult.results.rspamd.symbols).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Triggered Rules ({Object.keys(scanResult.results.rspamd.symbols).length})</p>
                    <div className="max-h-96 overflow-y-auto border rounded divide-y">
                      {Object.entries(scanResult.results.rspamd.symbols).sort(([,a],[,b]) => Math.abs(b.score) - Math.abs(a.score)).map(([sym, data]) => (
                        <button key={sym} onClick={() => openSymbolDetails(sym, data, 'rspamd')} className="w-full text-left px-3 py-2 hover:bg-gray-50 transition text-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="font-medium truncate">{sym}</span>
                              {hasRec(sym, 'rspamd') && <Lightbulb className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />}
                            </div>
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${data.score > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {data.score > 0 ? '+' : ''}{data.score.toFixed(2)}
                            </span>
                          </div>
                          {data.description && <p className="text-xs text-gray-400 truncate mt-0.5">{data.description}</p>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              {scanResult.results?.ai ? (
                <>
                  <h4 className="font-semibold text-purple-600 mb-3">AI (GPT) Analysis</h4>
                  <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                    <div><span className="text-gray-500 block">Probability</span><span className="text-lg font-bold">{scanResult.results.ai.probability != null ? (scanResult.results.ai.probability * 100).toFixed(1) + '%' : 'N/A'}</span></div>
                    <div><span className="text-gray-500 block">Classification</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${scanResult.results.ai.is_spam ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{scanResult.results.ai.is_spam ? 'SPAM' : 'HAM'}</span></div>
                  </div>
                  {scanResult.results.ai.reasoning && (
                    <div className="border-l-4 border-purple-500 bg-gray-50 p-3 rounded text-sm italic mb-3">"{scanResult.results.ai.reasoning}"</div>
                  )}
                  {scanResult.results.ai.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1">{scanResult.results.ai.categories.map((c, i) => <span key={i} className="px-2 py-0.5 rounded-full text-xs border border-purple-200 text-purple-700">{c.replace('GPT_', '')}</span>)}</div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <h4 className="font-semibold text-gray-400 mb-1">AI Analysis</h4>
                  <p className="text-sm text-gray-400">Not available for this scan</p>
                </div>
              )}
            </div>

            {/* SpamAssassin */}
            {scanResult.results?.spamassassin && (
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-yellow-600">SpamAssassin Results</h4>
                  {recCount('spamassassin', scanResult.results.spamassassin.symbols) > 0 && (
                    <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"><Lightbulb className="w-3 h-3" />{recCount('spamassassin', scanResult.results.spamassassin.symbols)} tips</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                  <div><span className="text-gray-500 block">Score</span><span className="text-lg font-bold">{scanResult.results.spamassassin.score?.toFixed(2) || '0'}</span></div>
                  <div><span className="text-gray-500 block">Required</span><span className="text-lg font-bold">{scanResult.results.spamassassin.required_score || '5.0'}</span></div>
                  <div><span className="text-gray-500 block">Rules</span><span className="text-lg font-bold">{scanResult.results.spamassassin.symbols?.length || 0}</span></div>
                  <div><span className="text-gray-500 block">Status</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${scanResult.results.spamassassin.is_spam ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{scanResult.results.spamassassin.is_spam ? 'SPAM' : 'CLEAN'}</span></div>
                </div>
                {scanResult.results.spamassassin.symbols?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Triggered Rules ({scanResult.results.spamassassin.symbols.length})</p>
                    <div className="max-h-96 overflow-y-auto border rounded divide-y">
                      {scanResult.results.spamassassin.symbols.map((sym, idx) => {
                        const det = scanResult.results.spamassassin.symbol_details?.[sym] || {};
                        const hasScore = det.score !== 'N/A' && det.score !== undefined;
                        return (
                          <button key={idx} onClick={() => openSymbolDetails(sym, det, 'spamassassin')} className="w-full text-left px-3 py-2 hover:bg-gray-50 transition text-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="font-medium truncate">{sym}</span>
                                {hasRec(sym, 'spamassassin') && <Lightbulb className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />}
                              </div>
                              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${hasScore && det.score > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {hasScore ? (det.score > 0 ? '+' : '') + parseFloat(det.score).toFixed(2) : 'N/A'}
                              </span>
                            </div>
                            {det.description && <p className="text-xs text-gray-400 truncate mt-0.5">{det.description}</p>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Symbol details modal */}
        {symbolDetailsModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeSymbolDetails}>
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold">{symbolDetailsModal.symbol}</h3>
                  <p className="text-sm text-gray-500">Engine: {symbolDetailsModal.engine}</p>
                </div>
                {symbolRecommendation && <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"><Lightbulb className="w-3 h-3" />Recommendations</span>}
              </div>
              <hr className="mb-4" />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-blue-600 mb-3">Technical Details</h4>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><span className="text-gray-500 text-sm">Score</span><p className="text-lg font-bold">{symbolDetailsModal.data?.score || 0}</p></div>
                    {symbolDetailsModal.data?.metric_score && <div><span className="text-gray-500 text-sm">Metric</span><p className="text-lg font-bold">{symbolDetailsModal.data.metric_score}</p></div>}
                  </div>
                  {symbolDetailsModal.data?.description && <div className="bg-gray-50 p-3 rounded text-sm mb-3">{symbolDetailsModal.data.description}</div>}
                  {symbolDetailsModal.data?.options?.length > 0 && (
                    <div className="flex flex-wrap gap-1">{symbolDetailsModal.data.options.map((o, i) => <span key={i} className="px-2 py-0.5 rounded-full text-xs border">{o}</span>)}</div>
                  )}
                </div>
                <div>
                  {(loadingRecommendation || generatingRecommendation) ? (
                    <>
                      <h4 className="font-semibold text-purple-600 flex items-center gap-1 mb-3">
                        {generatingRecommendation ? <Sparkles className="w-4 h-4" /> : <Lightbulb className="w-4 h-4" />}
                        {generatingRecommendation ? 'Generating with GPT...' : 'How to Fix'}
                      </h4>
                      <div className="space-y-2"><div className="h-6 w-48 bg-gray-200 rounded animate-pulse" /><div className="h-16 bg-gray-200 rounded animate-pulse" /><div className="h-20 bg-gray-200 rounded animate-pulse" /></div>
                    </>
                  ) : symbolRecommendation ? (
                    <>
                      <h4 className="font-semibold text-purple-600 flex items-center gap-1 mb-3"><Lightbulb className="w-4 h-4" />How to Fix</h4>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold">{symbolRecommendation.title}</h5>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${symbolRecommendation.severity === 'critical' || symbolRecommendation.severity === 'high' ? 'bg-red-100 text-red-700' : symbolRecommendation.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{symbolRecommendation.severity?.toUpperCase()}</span>
                        </div>
                        {symbolRecommendation.category && <span className="text-xs border rounded-full px-2 py-0.5 mb-2 inline-block">{symbolRecommendation.category}</span>}
                        {symbolRecommendation.description && <div className="bg-gray-50 p-3 rounded text-sm mb-3"><span className="text-gray-500 block text-xs mb-1">What this means:</span>{symbolRecommendation.description}</div>}
                        <div className={`p-3 rounded text-sm border-l-4 ${symbolRecommendation.severity === 'critical' || symbolRecommendation.severity === 'high' ? 'border-red-500 bg-red-50' : symbolRecommendation.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'}`}>
                          <span className="text-gray-500 block text-xs mb-1">Recommended action:</span>{symbolRecommendation.recommendation}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <Sparkles className="w-10 h-10 text-purple-300 mb-2" />
                      <p className="text-sm text-gray-500 mb-3">No recommendation on file for this rule</p>
                      {isAdmin && (
                        <button onClick={generateRecommendation} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm font-medium">
                          <Sparkles className="w-4 h-4" /> Ask GPT to explain
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={closeSymbolDetails} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* History modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowHistory(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Scan History</h3>
                {isAdmin && scanUsers.length > 0 && (
                  <select
                    value={historyUserFilter}
                    onChange={e => { setHistoryUserFilter(e.target.value); fetchScanHistory(e.target.value); }}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                  >
                    <option value="">All users</option>
                    <option value="me">My scans</option>
                    {scanUsers.map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.email || u.name} ({u.scan_count})</option>
                    ))}
                  </select>
                )}
              </div>
              {scanHistory.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Subject</th>
                      <th className="px-3 py-2 text-left">Score</th>
                      <th className="px-3 py-2 text-left">Result</th>
                      <th className="px-3 py-2 text-left">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {scanHistory.map((s, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">{new Date(s.created_at || s.scan_date).toLocaleString()}</td>
                        <td className="px-3 py-2 truncate max-w-[200px]">{s.subject || 'Untitled'}</td>
                        <td className="px-3 py-2">{s.score?.toFixed?.(2) || '0.00'}</td>
                        <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.is_spam ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{s.is_spam ? 'SPAM' : 'CLEAN'}</span></td>
                        <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.scan_method === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{s.scan_method || 'manual'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8"><History className="w-12 h-12 text-gray-300 mx-auto mb-2" /><p className="text-gray-500">No scan history</p></div>
              )}
              <div className="flex justify-end mt-4"><button onClick={() => setShowHistory(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Close</button></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Scanner;
